import React, {
  useState,
  useEffect,
  useMemo,
  ReactElement,
  useCallback,
} from 'react'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import { useTranslation } from 'react-i18next'
import { addUpdateProgram, fetchProgramOptions } from '../../api/programs'
import { useNavigate, useLocation } from 'react-router'
import {
  FeeStructure,
  FeeType,
  Program,
  ProgramCommunityOption,
  ProgramOptions,
  ProgramStatus,
  ProgramType,
  ProgramUpdate,
} from '../../swagger'
import { sortCollatorMatchingLanguagesOf } from '../../i18n'
import dateUtilities, {
  dateToDashString,
  getDateWithZeroHourOffset,
  isSameDate,
  createSafeDate,
} from '../../utils/dateUtility'
import { isEmpty } from '../../utils/stringUtility'
import CardFormHeader from '../Card/CardFormHeader'
import { FormDivider } from '../Elements/FormDivider'
import ProgramFormCardButtons, {
  Action,
} from '../Buttons/ProgramFormCardButtons'
import { FieldValidity } from '../Interfaces/FieldValidity'
import AddressModal from '../Address/AddressModal'
import { useAuth } from '../Routes/AuthProvider'
import programTypeMap from '../../utils/programTypeMap'
import ProgramInformationFields from '../Programs/ProgramInformationFields'
import ProgramAddressFields from '../Programs/ProgramAddressFields'
import DropdownSelectOptions from '../Interfaces/DropdownSelectOptions'
import { jsDateFromYearMonthDayString } from '../../utils/jsDateFromYearMonthDayString'
import {
  ProgramFeeFields,
  ProgramFeeNames,
  ProgramFees,
} from '../Programs/ProgramFeeFields'
import TutorFormField from '../Programs/TutorFormField'
import useLoadingContext from '../../hooks/useLoadingContext'
import { OperationIds } from '../../swagger/operationIdEnum'
import LoadingProgress from '../Elements/LoadingProgress'
import { SelectChangeEvent } from '@mui/material/Select'
import { LCD, useCommunitiesContext } from '../Context/CommunitiesContext'
import { ACTING_AS_PARENT } from '../../utils/constants'
import BasicModal from '../Modals/BasicModal'
import { Box, Button } from '@mui/material'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import { useUnmountEffect } from '../../hooks/useUnmountEffect'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import {
  ICommunityInformation,
  ProgramFormCardProps,
  ProgramFormCardVariants,
  ProgramFormValidFields,
} from './ProgramFormCard/types'
import {
  calculateFourMonthsFromToday,
  filterActorSelectOption,
  isWithinProgramDateRange,
} from './ProgramFormCard/utils'
import {
  getDaysOfTheWeek,
  getProgramStatusLabel,
} from './ProgramFormCard/utils'
import {
  daysOfTheWeekMap,
  emptyProgramDetails,
  validFieldsInitialState,
  MAXIMUM_SEMESTER_DATE,
  MINIMUM_SEMESTER_DATE,
} from './ProgramFormCard/constants'
import { amountFromInput } from '../../utils/amountFromInput'
import {
  convertTwoDecimalStringToBig,
  isValidInputDecimal,
} from '../../utils/bigJSHelper'

const filename = 'ProgramFormCard'
const today = new Date()

const fourMonthsFromToday = calculateFourMonthsFromToday()
const fetchCommunitySpecificProgramOptionsLoadingId = `${filename}-${OperationIds.FetchCommunitySpecificProgramOptions}`
const fetchProgramOptionsLoadingId = `${filename}-${OperationIds.FetchProgramOptions}`

const ProgramFormCard: React.FC<ProgramFormCardProps> = (props) => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const { handleError, handleMessage } = useAppSnackbar()
  const navigate = useNavigate()
  const { permissionAbility, userDetails } = useAuth()
  const sortCollator = sortCollatorMatchingLanguagesOf(i18n)
  const useAddProgramVariant =
    props.variant === ProgramFormCardVariants.AddProgram

  const location: { search: string; state: { communityKey: string } } =
    useLocation()
  const {
    hasEnrollmentInvitesWithInProgressStatus,
    setEditForParent,
    isProgramDetailsLoading = false,
  } = props

  const daysOfTheWeek = useMemo(() => getDaysOfTheWeek(t), [t])
  const programStatusLabel = useMemo(() => getProgramStatusLabel(t), [t])

  const {
    directorOptions: directors,
    programTypesFeeOptions: programTypesFees,
    tutorOptions,
    setDirectorsAndTutorsForCommunitySelection,
    selectedCommunityKey,
    tutorModel,
    programModelsOptions,
    resetTutorOptions,
  } = useCommunitiesContext()

  useUnmountEffect(resetTutorOptions)

  const fetchErrorMessage = t(
    'Programs.ProgramForm.Error.FetchProgramOptions',
    'Error occurred retrieving program options.'
  )

  const fetchErrorMessageDirectorsAndTutorsOptions = t(
    'Programs.ProgramForm.Error.FetchDirectorsAndTutorsOptions',
    'Error occurred retrieving director and tutor options.'
  )

  if (useAddProgramVariant) {
    if (location?.state) {
      //It prevents warning in the console
      const { communityKey, communityName } =
        location.state as ICommunityInformation
      props.programDetails.communityId = +communityKey
      props.programDetails.communityName = communityName
    }
  }

  /** State Objects */
  const [cancelInitiated, setCancelInitiated] = useState(false)

  const [programDetails, setProgramDetails] = useState<Program>(
    props.programDetails ?? {
      ...emptyProgramDetails,
      // Since load goes to the component, the previous today and fourMonthsFromToday are no longer in emptyProgramDetails, so add them here now.
      semesterOneStartDate: today,
      semesterTwoStartDate: fourMonthsFromToday,
      tutorModel,
    }
  )
  const [isLoadingProgramFormCard, setLoadingProgramFormCard] = useState(
    isProgramDetailsLoading
  )
  const [hideFromPublicSearch, setHideFromPublicSearch] = useState<boolean>(
    props.programDetails.hideFromPublicSearch ?? false
  )
  const [programType, setProgramType] = useState<ProgramType>()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    action: Action
    status: ProgramStatus
  } | null>(null)

  const [hasChangeFees, setHasChangeFees] = useState(false)
  const [showFeeModalWarning, setShowFeeModalWarning] = useState(false)

  const firstSemesterDate = getDateWithZeroHourOffset(
    props.programDetails.semesterOneStartDate
  )

  const secondSemesterDate = getDateWithZeroHourOffset(
    props.programDetails.semesterTwoStartDate
  )

  /**
   * dateToDashString removes time zone offset part of the date, which is +X hours e.g.
   *  Sun Jan 30 2023 18:00:00 GMT-0600 (Central Standard Time)
   *  becomes 2023-01-30 since we provide isUTC?: true
   */
  const semesterOneDate =
    dateToDashString(firstSemesterDate, true) ?? dateToDashString(today, true)

  const semesterTwoDate =
    dateToDashString(secondSemesterDate, true) ??
    dateToDashString(fourMonthsFromToday, true)

  const [firstSemesterStartDate, setFirstSemesterStartDate] =
    useState(semesterOneDate)
  const [secondSemesterStartDate, setSecondSemesterStartDate] =
    useState(semesterTwoDate)
  const [previousProgramType, setPreviousProgramType] = useState(
    props.programDetails.type ?? emptyProgramDetails.type
  )
  const [previousCommunityId, setPreviousCommunityId] = useState(
    props.programDetails.communityId ?? emptyProgramDetails.communityId
  )
  const [isFieldDisabled, setIsFieldDisabled] = useState(!useAddProgramVariant)
  const [tutorFormFields, setTutorFormFields] = useState<ReactElement[]>([])
  const [fieldIdToRemove, setFieldIdToRemove] = useState('')
  const [tutorActorIdMap, setTutorActorIdMap] = useState(
    new Map<string, number>()
  )
  const [tutorSelectionOptions, setTutorSelectionOptions] = useState<
    DropdownSelectOptions[]
  >([])

  /**
   * initialFees: defines form `fees` initial state
   * is used to restore fees, is modified as we handle change events on `handleInputChange`
   */
  const initialFees: ProgramFees = {
    [ProgramFeeNames.ApplicationFee]:
      props.programDetails.applicationFee ?? '0.00',
    [ProgramFeeNames.Tuition]: props.programDetails.tuition ?? '0.00',
    [ProgramFeeNames.EnrollmentFee]:
      props.programDetails.enrollmentFee ?? '0.00',
    [ProgramFeeNames.SupplyFeeFirstStudent]:
      props.programDetails.supplyFeeFirstStudent ?? '0.00',
    [ProgramFeeNames.SupplyFeeAdditionalStudent]:
      props.programDetails.supplyFeeAdditionalStudent ?? '0.00',
    [ProgramFeeNames.LocalFeeFirstStudent]:
      props.programDetails.localFeeFirstStudent ?? '0.00',
    [ProgramFeeNames.LocalFeeAdditionalStudent]:
      props.programDetails.localFeeAdditionalStudent ?? '0.00',
    [ProgramFeeNames.FacilityFeeFirstStudent]:
      props.programDetails.facilityFeeFirstStudent ?? '0.00',
    [ProgramFeeNames.FacilityFeeAdditionalStudent]:
      props.programDetails.facilityFeeAdditionalStudent ?? '0.00',
    [ProgramFeeNames.MiscFeeFirstStudent]:
      props.programDetails.miscFeeFirstStudent ?? '0.00',
    [ProgramFeeNames.MiscFeeAdditionalStudent]:
      props.programDetails.miscFeeAdditionalStudent ?? '0.00',
    [ProgramFeeNames.SubLicensedTutorPercentage]:
      props.programDetails.subLicensedTutorPercentage ?? '0.00',
    [ProgramFeeNames.SemesterOneLicensingFee]:
      props.programDetails.semesterOneLicensingFee ?? '0.00',
    [ProgramFeeNames.DiscountSemesterOneLicensingFee]:
      props.programDetails.discountSemesterOneLicensingFee ?? '0.00',
    [ProgramFeeNames.SemesterTwoLicensingFee]:
      props.programDetails.semesterTwoLicensingFee ?? '0.00',
    [ProgramFeeNames.DiscountSemesterTwoLicensingFee]:
      props.programDetails.discountSemesterTwoLicensingFee ?? '0.00',
    [ProgramFeeNames.MultiStudentApplicationDiscount]:
      props.programDetails.multiStudentApplicationDiscount ?? '0.00',
    [ProgramFeeNames.InvitationFee]:
      props.programDetails.invitationFee ?? '0.00',
    [ProgramFeeNames.DiscountInvitationFee]:
      props.programDetails.discountInvitationFee ?? '0.00',
    [ProgramFeeNames.TutorS1FirstStudentLicFee]:
      props.programDetails.tutorS1FirstStudentLicFee ?? '0.00',
    [ProgramFeeNames.TutorS1AdditionalStudentLicFee]:
      props.programDetails.tutorS1AdditionalStudentLicFee ?? '0.00',
    [ProgramFeeNames.TutorS2FirstStudentLicFee]:
      props.programDetails.tutorS2FirstStudentLicFee ?? '0.00',
    [ProgramFeeNames.TutorS2AdditionalStudentLicFee]:
      props.programDetails.tutorS2AdditionalStudentLicFee ?? '0.00',
    [ProgramFeeNames.LcdTutorPercentage]:
      props.programDetails.lcdTutorPercentage ?? '0.00',
  }

  const [fees, setFees] = useState<ProgramFees>(initialFees)
  const [status, setStatus] = useState(programDetails.status)
  const [programOptions, setProgramOptions] = useState<ProgramOptions>()
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [optionsFetched, setOptionsFetched] = useState(false)
  const [loadCommunityProgramOptions, setLoadCommunityProgramOptions] =
    useState(useAddProgramVariant)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [address, setAddress] = useState(programDetails.address)
  const [isAddressValid, setIsAddressValid] = useState(true)
  const [validFields, setValidFields] = useState<ProgramFormValidFields>(
    validFieldsInitialState
  )
  const [isCancel, setIsCancel] = useState(false)
  const [loading, setLoading] = useState(false)
  const [programTypes, setProgramTypes] = useState<ProgramType[]>([
    props.programDetails.type,
  ])
  const [loadProgramOptions, setLoadProgramOptions] = useState(false)
  const [doCheckProgramFeesExist, setDoCheckProgramFeesExist] = useState(true)
  const [loadedProgramDetails, setLoadedProgramDetails] = useState(false)

  const communities = programOptions?.communities?.sort((a, b) => {
    return sortCollator.compare(
      a.name ?? fallbackForCommunityName(a),
      b.name ?? fallbackForCommunityName(b)
    )
  })

  // Our assumption is unique community names per https://projekt202.atlassian.net/browse/CCP1-953
  // Use useMemo here so useEffect below isn't rendering unnecessarily
  const communitiesMap = useMemo(() => {
    const map = new Map<
      string /** communityName */,
      ProgramCommunityOption /** Community Program Option */
    >()
    communities?.forEach((community) => {
      const communityName =
        community.name ?? fallbackForCommunityName(community)
      if (!map.has(communityName)) {
        map.set(communityName, community)
      }
    })
    return map
  }, [communities])

  const onTutorFieldRemove = useCallback((tutorFieldKey: string) => {
    setTutorActorIdMap((currentMap) => {
      currentMap.delete(tutorFieldKey)
      return currentMap
    })
  }, [])

  const removeTutorField = useCallback(
    (fieldId: string, tutorFieldKey: string) => {
      setFieldIdToRemove(fieldId)
      onTutorFieldRemove(tutorFieldKey)
    },
    [onTutorFieldRemove]
  )

  const onTutorChange = useCallback(
    (tutorKey: number, event: SelectChangeEvent<unknown>) => {
      setTutorActorIdMap((currentMap) => {
        currentMap.set(event.target.name as string, tutorKey)
        return currentMap
      })
      setTutorFormFields((currentTutorFields) => {
        const fieldIndexToUpdate = currentTutorFields.findIndex((field) => {
          return field.props.tutorFormKey === (event.target.name as string)
        })

        if (fieldIndexToUpdate === -1) return currentTutorFields

        const tutorFields = [...currentTutorFields]
        tutorFields[fieldIndexToUpdate] = {
          ...tutorFields[fieldIndexToUpdate],
          props: {
            ...tutorFields[fieldIndexToUpdate].props,
            tutorName: event.target.name,
          },
        }
        return tutorFields
      })
    },
    []
  )

  const setDateFieldsForCommunitySelection = useCallback(
    (community: ProgramCommunityOption) => {
      const defaultFirstDate = community.defaultSemesterOneStartDate

      const defaultSecondDate = community.defaultSemesterTwoStartDate

      if (defaultSecondDate <= today) {
        defaultSecondDate.setFullYear(defaultFirstDate.getUTCFullYear() + 1)
      }

      // ensure we pass UTC dates to dateToDashString
      const firstSemesterDate = new Date(
        defaultFirstDate.getUTCFullYear(),
        defaultFirstDate.getUTCMonth(),
        defaultFirstDate.getUTCDate()
      )

      const secondSemesterDate = new Date(
        defaultSecondDate.getUTCFullYear(),
        defaultSecondDate.getUTCMonth(),
        defaultSecondDate.getUTCDate()
      )

      setFirstSemesterStartDate(
        !!community
          ? dateToDashString(firstSemesterDate, true)
          : firstSemesterStartDate
      )

      setSecondSemesterStartDate(
        !!community
          ? dateToDashString(secondSemesterDate, true)
          : secondSemesterStartDate
      )
    },
    [firstSemesterStartDate, secondSemesterStartDate]
  )
  const fetchedDirectorsAndTutors = async (): Promise<void> => {
    try {
      const communityKey = programDetails.communityId
      await setDirectorsAndTutorsForCommunitySelection(communityKey)
      // The communitiesMap is not set when initially loading Add Program, so we load these after indicating fetchCommunityOptions is called and we setup the communitiesMap.
      const communityProgramOption = communitiesMap.get(
        programDetails.communityName ?? ''
      )
      if (!!communityProgramOption) {
        setDateFieldsForCommunitySelection(communityProgramOption)
      }
      setOptionsFetched(true)
    } catch (error) {
      await handleError(error, fetchErrorMessageDirectorsAndTutorsOptions)
    }
  }

  const {
    triggerFetch: triggerFetchCommunitySpecificProgramOptions,
    addLoadingIds,
    loadingIds,
  } = useLoadingContext({
    loadingId: fetchCommunitySpecificProgramOptionsLoadingId,
    asyncFunction: fetchedDirectorsAndTutors,
  })

  /**
   * Given our programDetails were passed in, set the appropriate state objects
   * so fields have properly formatted data for editing a program.
   */
  useEffect(() => {
    if (!!props.programDetails && !loadedProgramDetails) {
      setProgramDetails({
        ...props.programDetails,
        startTime: !!props.programDetails.startTime
          ? props.programDetails.startTime
          : '08:00',
        endTime: !!props.programDetails.endTime
          ? props.programDetails.endTime
          : '15:00',
        dayOfTheWeek:
          !props.programDetails.dayOfTheWeek ||
          props.programDetails.dayOfTheWeek === 7
            ? 0
            : props.programDetails.dayOfTheWeek,
        tutorModel: props.programDetails.tutorModel
          ? props.programDetails.tutorModel
          : tutorModel,
      })
      setProgramType(props.programDetails.type)
      setFees({
        ...fees,
        [ProgramFeeNames.ApplicationFee]:
          props.programDetails.applicationFee ?? fees.applicationFee,
        [ProgramFeeNames.Tuition]: props.programDetails.tuition ?? fees.tuition,
        [ProgramFeeNames.LocalFeeFirstStudent]:
          props.programDetails.localFeeFirstStudent ??
          fees.localFeeFirstStudent,
        [ProgramFeeNames.LocalFeeAdditionalStudent]:
          props.programDetails.localFeeAdditionalStudent ??
          fees.localFeeAdditionalStudent,
        [ProgramFeeNames.FacilityFeeFirstStudent]:
          props.programDetails.facilityFeeFirstStudent ??
          fees.facilityFeeFirstStudent,
        [ProgramFeeNames.FacilityFeeAdditionalStudent]:
          props.programDetails.facilityFeeAdditionalStudent ??
          fees.facilityFeeAdditionalStudent,
        [ProgramFeeNames.SupplyFeeFirstStudent]:
          props.programDetails.supplyFeeFirstStudent ??
          fees.supplyFeeFirstStudent,
        [ProgramFeeNames.SupplyFeeAdditionalStudent]:
          props.programDetails.supplyFeeAdditionalStudent ??
          fees.supplyFeeAdditionalStudent,
        [ProgramFeeNames.EnrollmentFee]:
          props.programDetails.enrollmentFee ?? fees.enrollmentFee,
        [ProgramFeeNames.MiscFeeFirstStudent]:
          props.programDetails.miscFeeFirstStudent ?? fees.miscFeeFirstStudent,
        [ProgramFeeNames.MiscFeeAdditionalStudent]:
          props.programDetails.miscFeeAdditionalStudent ??
          fees.miscFeeAdditionalStudent,
        [ProgramFeeNames.SubLicensedTutorPercentage]:
          props.programDetails.subLicensedTutorPercentage ??
          fees.subLicensedTutorPercentage,
        [ProgramFeeNames.SemesterOneLicensingFee]:
          props.programDetails.semesterOneLicensingFee ??
          fees.semesterOneLicensingFee,
        [ProgramFeeNames.DiscountSemesterOneLicensingFee]:
          props.programDetails.discountSemesterOneLicensingFee ??
          fees.discountSemesterOneLicensingFee,
        [ProgramFeeNames.SemesterTwoLicensingFee]:
          props.programDetails.semesterTwoLicensingFee ??
          fees.semesterTwoLicensingFee,
        [ProgramFeeNames.DiscountSemesterTwoLicensingFee]:
          props.programDetails.discountSemesterTwoLicensingFee ??
          fees.discountSemesterTwoLicensingFee,
        [ProgramFeeNames.MultiStudentApplicationDiscount]:
          props.programDetails.multiStudentApplicationDiscount ??
          fees.multiStudentApplicationDiscount,
        [ProgramFeeNames.InvitationFee]:
          props.programDetails.invitationFee ?? fees.invitationFee,
        [ProgramFeeNames.DiscountInvitationFee]:
          props.programDetails.discountInvitationFee ??
          fees.discountInvitationFee,
        [ProgramFeeNames.TutorS1FirstStudentLicFee]:
          props.programDetails.tutorS1FirstStudentLicFee ??
          fees.tutorS1FirstStudentLicFee,
        [ProgramFeeNames.TutorS1AdditionalStudentLicFee]:
          props.programDetails.tutorS1AdditionalStudentLicFee ??
          fees.tutorS1AdditionalStudentLicFee,
        [ProgramFeeNames.TutorS2FirstStudentLicFee]:
          props.programDetails.tutorS2FirstStudentLicFee ??
          fees.tutorS2FirstStudentLicFee,
        [ProgramFeeNames.TutorS2AdditionalStudentLicFee]:
          props.programDetails.tutorS2AdditionalStudentLicFee ??
          fees.tutorS2AdditionalStudentLicFee,
        [ProgramFeeNames.LcdTutorPercentage]:
          props.programDetails.lcdTutorPercentage ?? fees.lcdTutorPercentage,
      })
      setLoadedProgramDetails(true)
      setPreviousProgramType(props.programDetails.type)
      setPreviousCommunityId(props.programDetails.communityId)
      if (!!props.programDetails.semesterOneStartDate) {
        setFirstSemesterStartDate(
          dateToDashString(props.programDetails.semesterOneStartDate, true)
        )
      }
      if (!!props.programDetails.semesterTwoStartDate) {
        setSecondSemesterStartDate(
          dateToDashString(props.programDetails.semesterTwoStartDate, true)
        )
      }
      setStatus(props.programDetails.status)
      setAddress(props.programDetails.address)

      if (userDetails.actingAs !== ACTING_AS_PARENT) {
        triggerFetchCommunitySpecificProgramOptions()
      }
    }
  }, [
    triggerFetchCommunitySpecificProgramOptions,
    props.programDetails,
    loadedProgramDetails,
    fees,
    communitiesMap,
    useAddProgramVariant,
    props.programDetails.directorEmail,
    props.programDetails.communityId,
    props.programDetails.tutorModel,
    fetchErrorMessageDirectorsAndTutorsOptions,
    addLoadingIds,
    userDetails.actingAs,
    tutorModel,
  ])

  useEffect(() => {
    if (
      useAddProgramVariant &&
      communitiesMap.size > 0 &&
      (previousCommunityId < 0 || isFirstLoad)
    ) {
      const communityProgramOption = communitiesMap.get(
        props.programDetails.communityName ?? ''
      )
      if (!!communityProgramOption) {
        setDateFieldsForCommunitySelection(communityProgramOption)
        setIsFirstLoad(false)
      }
    }
  }, [
    useAddProgramVariant,
    communitiesMap,
    props.programDetails.communityName,
    setDateFieldsForCommunitySelection,
    previousCommunityId,
    isFirstLoad,
  ])

  const setAddressFieldsForCommunitySelection = useCallback(
    (community: ProgramCommunityOption) => {
      const newAddress = {
        ...community.address,
        streetAddress2: community.address?.streetAddress2 ?? '',
        state: community.address?.state ?? '',
        zip: community.address?.zip ?? '',
      }
      setAddress(newAddress)
      setProgramDetails({
        ...programDetails,
        address: newAddress,
      })
      setIsAddressValid(true)
    },
    [programDetails]
  )

  useEffect(() => {
    if (optionsFetched && communitiesMap.size > 0) {
      const communityProgramOption = communitiesMap.get(
        programDetails.communityName ?? ''
      )
      if (!!communityProgramOption && !programDetails.address.countryCode) {
        setAddressFieldsForCommunitySelection(communityProgramOption)
      }
      setOptionsFetched(false)
    }
  }, [
    communitiesMap,
    optionsFetched,
    programDetails.address.countryCode,
    programDetails.communityName,
    setAddressFieldsForCommunitySelection,
    setDateFieldsForCommunitySelection,
  ])

  /** When we update the communityId but the states have yet to update, don't add the loadingId until the state updated so we call the endpoint with the new communityId */
  useEffect(() => {
    if (
      (previousCommunityId > 0 || !useAddProgramVariant) &&
      previousCommunityId !== programDetails.communityId &&
      loadCommunityProgramOptions &&
      userDetails.actingAs !== ACTING_AS_PARENT
    ) {
      setLoadCommunityProgramOptions(false)
      triggerFetchCommunitySpecificProgramOptions()
    }
  }, [
    addLoadingIds,
    triggerFetchCommunitySpecificProgramOptions,
    loadCommunityProgramOptions,
    previousCommunityId,
    programDetails.communityId,
    props.programDetails.communityId,
    useAddProgramVariant,
    userDetails.actingAs,
  ])

  const programFeeStructureMap = useMemo(() => {
    const feeStructureMap = new Map<string, FeeStructure[]>()
    programTypesFees?.programFeeStructures.forEach((programFeeStructure) => {
      feeStructureMap.set(
        programFeeStructure.programType,
        programFeeStructure.feeStructures
      )
    })
    return feeStructureMap
  }, [programTypesFees?.programFeeStructures])

  const programFeeMinimaMap = useMemo(() => {
    const feeMap = new Map<
      string,
      { minimum: string; default: string; allowEdit: boolean }
    >()
    programFeeStructureMap.get(programDetails.type)?.forEach((fee) => {
      // Translate feeType enum to Program object fee names
      let feeKey: string = fee.feeType
      if (fee.feeType === FeeType.MiscFeeFirstStudent) {
        feeKey = 'miscFeeFirstStudent'
      } else {
        Object.values(ProgramFeeNames).forEach((it) => {
          if (new RegExp(fee.feeType).test(it)) {
            feeKey = it
          }
        })
      }
      // Set the minimums on the map
      feeMap.set(feeKey, {
        minimum: fee.minimumAmount,
        default: fee.defaultTotalAmount,
        allowEdit: fee.allowEditTotal,
      })
    })
    return feeMap
  }, [programFeeStructureMap, programDetails.type])

  // Can update these to be the same function
  const filteredTutorOptions = useMemo(() => {
    return filterActorSelectOption(tutorOptions ?? [], {
      type: programDetails.type,
      firstSemesterStartDate,
      directorActorId: programDetails.directorActorId,
      removeExpired: false,
      isTutor: true,
    })
  }, [
    firstSemesterStartDate,
    tutorOptions,
    programDetails.type,
    programDetails.directorActorId,
  ])

  const tutorModelsOptions = useMemo<{ value: string; label: string }[]>(() => {
    return (programModelsOptions ?? []).map((tutor) => {
      return { value: tutor, label: tutor }
    })
  }, [programModelsOptions])

  // Can update these to be the same function
  const filteredDirectors = filterActorSelectOption(directors ?? [], {
    type: programDetails.type,
    firstSemesterStartDate,
    communityKey: programDetails.communityId,
    removeExpired: true,
  }).map((director) => {
    return {
      ...director,
      disabled: director.expired,
    }
  })

  const directorsMap = new Map<
    string /** key: director */,
    number /** value: directorActorId */
  >()
  filteredDirectors?.forEach((director) => {
    directorsMap.set(
      `${director.name} - ${director.actorKey}`,
      director.actorKey
    )
  })

  useEffect(() => {
    if (useAddProgramVariant) {
      setLoadProgramOptions(true)
      if (!!programOptions) {
        setLoadProgramOptions(false)
      }
    }
  }, [loadProgramOptions, useAddProgramVariant, programOptions])

  /**
   * Load the program options once. Set the necessary objects from the response
   * to reference when new selections are made.
   */
  useEffect(() => {
    if (!loadProgramOptions) return
    addLoadingIds([fetchProgramOptionsLoadingId])
  }, [loadProgramOptions, addLoadingIds])

  const fetchUserProgramOptions = async (): Promise<void> => {
    try {
      const fetchedProgramOptions = await fetchProgramOptions()
      setProgramOptions(fetchedProgramOptions)
      setProgramTypes(fetchedProgramOptions.programTypes)
      setLoadProgramOptions(false)
    } catch (error) {
      await handleError(error, fetchErrorMessage)
    }
  }
  useLoadingContext({
    loadingId: fetchProgramOptionsLoadingId,
    asyncFunction: fetchUserProgramOptions,
  })

  /**
   *  When we load Program Fees for a given community/program and we DO NOT
   *  have fees already set, update with the default fees if they exist and
   *  the minimum fee if default doesn't exist
   */
  useEffect(() => {
    if (programFeeMinimaMap.size > 0 && doCheckProgramFeesExist) {
      setFees({
        [ProgramFeeNames.ApplicationFee]:
          typeof programDetails.applicationFee === 'string'
            ? fees.applicationFee
            : programFeeMinimaMap.get(ProgramFeeNames.ApplicationFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.ApplicationFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.Tuition]:
          typeof programDetails.tuition === 'string'
            ? fees.tuition
            : programFeeMinimaMap.get(ProgramFeeNames.Tuition)?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.Tuition)?.minimum ??
              '0.00',
        [ProgramFeeNames.LocalFeeFirstStudent]:
          typeof programDetails.localFeeFirstStudent === 'string'
            ? fees.localFeeFirstStudent
            : programFeeMinimaMap.get(ProgramFeeNames.LocalFeeFirstStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.LocalFeeFirstStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.LocalFeeAdditionalStudent]:
          typeof programDetails.localFeeAdditionalStudent === 'string'
            ? fees.localFeeAdditionalStudent
            : programFeeMinimaMap.get(ProgramFeeNames.LocalFeeAdditionalStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.LocalFeeAdditionalStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.FacilityFeeFirstStudent]:
          typeof programDetails.facilityFeeFirstStudent === 'string'
            ? fees.facilityFeeFirstStudent
            : programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeFirstStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeFirstStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.FacilityFeeAdditionalStudent]:
          typeof programDetails.facilityFeeAdditionalStudent === 'string'
            ? fees.facilityFeeAdditionalStudent
            : programFeeMinimaMap.get(
                ProgramFeeNames.FacilityFeeAdditionalStudent
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.FacilityFeeAdditionalStudent
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.SupplyFeeFirstStudent]:
          typeof programDetails.supplyFeeFirstStudent === 'string'
            ? fees.supplyFeeFirstStudent
            : programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeFirstStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeFirstStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.SupplyFeeAdditionalStudent]:
          typeof programDetails.supplyFeeFirstStudent === 'string'
            ? fees.supplyFeeAdditionalStudent
            : programFeeMinimaMap.get(
                ProgramFeeNames.SupplyFeeAdditionalStudent
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.SupplyFeeAdditionalStudent
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.EnrollmentFee]:
          typeof programDetails.enrollmentFee === 'string'
            ? fees.enrollmentFee
            : programFeeMinimaMap.get(ProgramFeeNames.EnrollmentFee)?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.EnrollmentFee)?.minimum ??
              '0.00',
        [ProgramFeeNames.MiscFeeFirstStudent]:
          typeof programDetails.miscFeeFirstStudent === 'string'
            ? fees.miscFeeFirstStudent
            : programFeeMinimaMap.get(ProgramFeeNames.MiscFeeFirstStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.MiscFeeFirstStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.MiscFeeAdditionalStudent]:
          typeof programDetails.miscFeeAdditionalStudent === 'string'
            ? fees.miscFeeAdditionalStudent
            : programFeeMinimaMap.get(ProgramFeeNames.MiscFeeAdditionalStudent)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.MiscFeeAdditionalStudent)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.SubLicensedTutorPercentage]:
          typeof programDetails.subLicensedTutorPercentage === 'string'
            ? fees.subLicensedTutorPercentage
            : programFeeMinimaMap.get(
                ProgramFeeNames.SubLicensedTutorPercentage
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.SubLicensedTutorPercentage
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.LcdTutorPercentage]:
          typeof programDetails.lcdTutorPercentage === 'string'
            ? fees.lcdTutorPercentage
            : programFeeMinimaMap.get(ProgramFeeNames.LcdTutorPercentage)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.LcdTutorPercentage)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.SemesterOneLicensingFee]:
          typeof programDetails.semesterOneLicensingFee === 'string'
            ? fees.semesterOneLicensingFee
            : programFeeMinimaMap.get(ProgramFeeNames.SemesterOneLicensingFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.SemesterOneLicensingFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.DiscountSemesterOneLicensingFee]:
          typeof programDetails.discountSemesterOneLicensingFee === 'string'
            ? fees.discountSemesterOneLicensingFee
            : programFeeMinimaMap.get(
                ProgramFeeNames.DiscountSemesterOneLicensingFee
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.DiscountSemesterOneLicensingFee
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.SemesterTwoLicensingFee]:
          typeof programDetails.semesterTwoLicensingFee === 'string'
            ? fees.semesterTwoLicensingFee
            : programFeeMinimaMap.get(ProgramFeeNames.SemesterTwoLicensingFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.SemesterTwoLicensingFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.DiscountSemesterTwoLicensingFee]:
          typeof programDetails.discountSemesterTwoLicensingFee === 'string'
            ? fees.discountSemesterTwoLicensingFee
            : programFeeMinimaMap.get(
                ProgramFeeNames.DiscountSemesterTwoLicensingFee
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.DiscountSemesterTwoLicensingFee
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.MultiStudentApplicationDiscount]:
          typeof programDetails.multiStudentApplicationDiscount === 'string'
            ? fees.multiStudentApplicationDiscount
            : programFeeMinimaMap.get(
                ProgramFeeNames.MultiStudentApplicationDiscount
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.MultiStudentApplicationDiscount
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.InvitationFee]:
          typeof programDetails.invitationFee === 'string'
            ? fees.invitationFee
            : programFeeMinimaMap.get(ProgramFeeNames.InvitationFee)?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.InvitationFee)?.minimum ??
              '0.00',
        [ProgramFeeNames.DiscountInvitationFee]:
          typeof programDetails.discountInvitationFee === 'string'
            ? fees.discountInvitationFee
            : programFeeMinimaMap.get(ProgramFeeNames.DiscountInvitationFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.DiscountInvitationFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.TutorS1FirstStudentLicFee]:
          typeof programDetails.tutorS1FirstStudentLicFee === 'string'
            ? fees.tutorS1FirstStudentLicFee
            : programFeeMinimaMap.get(ProgramFeeNames.TutorS1FirstStudentLicFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.TutorS1FirstStudentLicFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.TutorS1AdditionalStudentLicFee]:
          typeof programDetails.tutorS1AdditionalStudentLicFee === 'string'
            ? fees.tutorS1AdditionalStudentLicFee
            : programFeeMinimaMap.get(
                ProgramFeeNames.TutorS1AdditionalStudentLicFee
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.TutorS1AdditionalStudentLicFee
              )?.minimum ??
              '0.00',
        [ProgramFeeNames.TutorS2FirstStudentLicFee]:
          typeof programDetails.tutorS2FirstStudentLicFee === 'string'
            ? fees.tutorS2FirstStudentLicFee
            : programFeeMinimaMap.get(ProgramFeeNames.TutorS2FirstStudentLicFee)
                ?.default ??
              programFeeMinimaMap.get(ProgramFeeNames.TutorS2FirstStudentLicFee)
                ?.minimum ??
              '0.00',
        [ProgramFeeNames.TutorS2AdditionalStudentLicFee]:
          typeof programDetails.tutorS2AdditionalStudentLicFee === 'string'
            ? fees.tutorS2AdditionalStudentLicFee
            : programFeeMinimaMap.get(
                ProgramFeeNames.TutorS2AdditionalStudentLicFee
              )?.default ??
              programFeeMinimaMap.get(
                ProgramFeeNames.TutorS2AdditionalStudentLicFee
              )?.minimum ??
              '0.00',
      })
      setDoCheckProgramFeesExist(false)
    }
  }, [programFeeMinimaMap, doCheckProgramFeesExist, programDetails, fees])

  /**
   * If we aren't canceling an edit and we have changed program types or
   * community and we have default/minimum fees to load then update the fee
   * fields to the new defaults/minimums
   */
  useEffect(() => {
    if (
      !isCancel &&
      (previousProgramType !== programDetails.type ||
        (programDetails.communityId > 0 &&
          programDetails.communityId !== previousCommunityId)) &&
      programFeeMinimaMap.size > 0
    ) {
      setFees({
        [ProgramFeeNames.ApplicationFee]:
          programFeeMinimaMap.get(ProgramFeeNames.ApplicationFee)?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.ApplicationFee)?.minimum ??
          '0.00',
        [ProgramFeeNames.Tuition]:
          programFeeMinimaMap.get(ProgramFeeNames.Tuition)?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.Tuition)?.minimum ??
          '0.00',
        [ProgramFeeNames.LocalFeeFirstStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.LocalFeeFirstStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.LocalFeeFirstStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.LocalFeeAdditionalStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.LocalFeeAdditionalStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.LocalFeeAdditionalStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.FacilityFeeFirstStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeFirstStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeFirstStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.FacilityFeeAdditionalStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeAdditionalStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.FacilityFeeAdditionalStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.SupplyFeeFirstStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeFirstStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeFirstStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.SupplyFeeAdditionalStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeAdditionalStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.SupplyFeeAdditionalStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.EnrollmentFee]:
          programFeeMinimaMap.get(ProgramFeeNames.EnrollmentFee)?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.EnrollmentFee)?.minimum ??
          '0.00',
        [ProgramFeeNames.MiscFeeFirstStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.MiscFeeFirstStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.MiscFeeFirstStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.MiscFeeAdditionalStudent]:
          programFeeMinimaMap.get(ProgramFeeNames.MiscFeeAdditionalStudent)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.MiscFeeAdditionalStudent)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.SubLicensedTutorPercentage]:
          programFeeMinimaMap.get(ProgramFeeNames.SubLicensedTutorPercentage)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.SubLicensedTutorPercentage)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.LcdTutorPercentage]:
          programFeeMinimaMap.get(ProgramFeeNames.LcdTutorPercentage)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.LcdTutorPercentage)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.SemesterOneLicensingFee]:
          programFeeMinimaMap.get(ProgramFeeNames.SemesterOneLicensingFee)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.SemesterOneLicensingFee)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.DiscountSemesterOneLicensingFee]:
          programFeeMinimaMap.get(
            ProgramFeeNames.DiscountSemesterOneLicensingFee
          )?.default ??
          programFeeMinimaMap.get(
            ProgramFeeNames.DiscountSemesterOneLicensingFee
          )?.minimum ??
          '0.00',
        [ProgramFeeNames.SemesterTwoLicensingFee]:
          programFeeMinimaMap.get(ProgramFeeNames.SemesterTwoLicensingFee)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.SemesterTwoLicensingFee)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.DiscountSemesterTwoLicensingFee]:
          programFeeMinimaMap.get(
            ProgramFeeNames.DiscountSemesterTwoLicensingFee
          )?.default ??
          programFeeMinimaMap.get(
            ProgramFeeNames.DiscountSemesterTwoLicensingFee
          )?.minimum ??
          '0.00',
        [ProgramFeeNames.MultiStudentApplicationDiscount]:
          programFeeMinimaMap.get(
            ProgramFeeNames.MultiStudentApplicationDiscount
          )?.default ??
          programFeeMinimaMap.get(
            ProgramFeeNames.MultiStudentApplicationDiscount
          )?.minimum ??
          '0.00',
        [ProgramFeeNames.InvitationFee]:
          programFeeMinimaMap.get(ProgramFeeNames.InvitationFee)?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.InvitationFee)?.minimum ??
          '0.00',
        [ProgramFeeNames.DiscountInvitationFee]:
          programFeeMinimaMap.get(ProgramFeeNames.DiscountInvitationFee)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.DiscountInvitationFee)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.TutorS1FirstStudentLicFee]:
          programFeeMinimaMap.get(ProgramFeeNames.TutorS1FirstStudentLicFee)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.TutorS1FirstStudentLicFee)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.TutorS1AdditionalStudentLicFee]:
          programFeeMinimaMap.get(
            ProgramFeeNames.TutorS1AdditionalStudentLicFee
          )?.default ??
          programFeeMinimaMap.get(
            ProgramFeeNames.TutorS1AdditionalStudentLicFee
          )?.minimum ??
          '0.00',
        [ProgramFeeNames.TutorS2FirstStudentLicFee]:
          programFeeMinimaMap.get(ProgramFeeNames.TutorS2FirstStudentLicFee)
            ?.default ??
          programFeeMinimaMap.get(ProgramFeeNames.TutorS2FirstStudentLicFee)
            ?.minimum ??
          '0.00',
        [ProgramFeeNames.TutorS2AdditionalStudentLicFee]:
          programFeeMinimaMap.get(
            ProgramFeeNames.TutorS2AdditionalStudentLicFee
          )?.default ??
          programFeeMinimaMap.get(
            ProgramFeeNames.TutorS2AdditionalStudentLicFee
          )?.minimum ??
          '0.00',
      })
    }
    // Ensure everything is reset before saying cancel is complete
    if (
      isCancel &&
      programDetails.communityId === props.programDetails.communityId &&
      previousCommunityId === props.programDetails.communityId &&
      programDetails.type === props.programDetails.type &&
      previousProgramType === props.programDetails.type
    ) {
      setIsCancel(false)
      if (useAddProgramVariant && userDetails.actingAs !== ACTING_AS_PARENT) {
        triggerFetchCommunitySpecificProgramOptions()
      }
    }
  }, [
    triggerFetchCommunitySpecificProgramOptions,
    programFeeMinimaMap,
    previousCommunityId,
    isCancel,
    previousProgramType,
    programDetails.type,
    props.programDetails.type,
    programDetails.communityId,
    props.programDetails.communityId,
    addLoadingIds,
    useAddProgramVariant,
    userDetails.actingAs,
  ])

  /** Inform the parent component if an edit is in progress for ConfirmNavigationAwayModal */
  useEffect(() => {
    if (!useAddProgramVariant) {
      setEditForParent?.(!isFieldDisabled)
    }
  }, [isFieldDisabled, setEditForParent, useAddProgramVariant])

  const isTutorFieldDisabled =
    isFieldDisabled ||
    !programDetails.director ||
    !programDetails.type ||
    filteredTutorOptions.length === 0

  useEffect(() => {
    if (
      !((loadedProgramDetails && !useAddProgramVariant) || useAddProgramVariant)
    ) {
      return
    }

    const getTutorLabel = (actorKey: number) => {
      return (
        tutorSelectionOptions.find((tutor) => tutor.id === actorKey)?.label ??
        ''
      )
    }

    const tutorFieldsArray: ReactElement[] = []

    if (programDetails?.tutors?.length && tutorSelectionOptions.length > 0) {
      programDetails.tutors?.forEach((tutor) => {
        const tutorActorKey = tutor?.actorKey ?? -1
        const valueForTutorField = getTutorLabel(tutorActorKey)
        tutorFieldsArray.push(
          <TutorFormField
            key={`tutorFormFieldKey-${tutorActorKey}`}
            tutors={tutorSelectionOptions}
            isDisabled={isTutorFieldDisabled}
            tutorFormKey={`tutorFormKey-${tutorActorKey}`}
            tutorFormId={`tutorFormId-${tutorActorKey}`}
            tutorName={valueForTutorField}
            removeTutorField={removeTutorField}
            onTutorChange={onTutorChange}
            programTutors={programDetails.tutors}
          />
        )
        setTutorActorIdMap((currentMap) => {
          const newMap = new Map(currentMap)
          newMap.set(`tutorFormKey-${tutorActorKey}`, tutorActorKey)
          return newMap
        })
      })
    } else if (!programDetails.tutors) {
      const dateTime = new Date().getTime()
      tutorFieldsArray.push(
        <TutorFormField
          key={`tutorFormFieldKey-${dateTime}`}
          tutors={tutorSelectionOptions}
          isDisabled={isTutorFieldDisabled}
          tutorFormKey={`tutorFormKey-${dateTime}`}
          tutorFormId={`tutorFormId-${dateTime}`}
          tutorName=""
          removeTutorField={removeTutorField}
          onTutorChange={onTutorChange}
          programTutors={programDetails?.tutors}
        />
      )
    }
    setTutorFormFields((prev) => {
      const prevKeys = prev.map((tutorField) => tutorField.key)
      const newKeys = tutorFieldsArray.map((tutorField) => tutorField.key)
      if (JSON.stringify(prevKeys) === JSON.stringify(newKeys)) {
        return prev /** does not change state */
      }
      return tutorFieldsArray /** update state if key are different */
    })
  }, [
    tutorSelectionOptions,
    isTutorFieldDisabled,
    programDetails.tutors,
    onTutorChange,
    removeTutorField,
    loadedProgramDetails,
    useAddProgramVariant,
  ])

  //Updating the fields when we swap from editable to uneditable field modes
  useEffect(() => {
    setTutorFormFields((currentTutorFormFields) =>
      currentTutorFormFields.map((tutorFormField: ReactElement) => {
        return {
          ...tutorFormField,
          props: {
            ...tutorFormField.props,
            tutors: tutorSelectionOptions,
            isDisabled: isTutorFieldDisabled,
          },
        }
      })
    )
  }, [tutorSelectionOptions, isTutorFieldDisabled])

  useEffect(() => {
    const haveFieldIdAndFieldsAreLoaded =
      !!fieldIdToRemove && tutorFormFields.length > 0
    if (haveFieldIdAndFieldsAreLoaded) {
      tutorFormFields.forEach((field, index) => {
        if (field.props.tutorFormId === fieldIdToRemove) {
          const modifiedTutorFields = [...tutorFormFields]
          modifiedTutorFields.splice(index, 1)
          setTutorFormFields(modifiedTutorFields)
        }
      })
      const tutorsFormProgramDetail = programDetails.tutors
      tutorsFormProgramDetail?.forEach((field, index) => {
        if (field.actorKey?.toString() === fieldIdToRemove.split('-')[1]) {
          const modifiedTutorsFormProgramDetail = [...tutorsFormProgramDetail]
          modifiedTutorsFormProgramDetail.splice(index, 1)
          setProgramDetails({
            ...props.programDetails,
            tutors: modifiedTutorsFormProgramDetail,
          })
        }
      })
      setFieldIdToRemove('')
    }
  }, [
    fieldIdToRemove,
    tutorFormFields,
    programDetails.tutors,
    props.programDetails,
  ])

  useEffect(() => {
    setTutorSelectionOptions(() => {
      return filteredTutorOptions.map((tutor) => {
        return {
          id: tutor.actorKey,
          label: tutor.name,
          disabled:
            (programDetails?.tutors?.some(
              (detailTutor) => tutor.actorKey === detailTutor.actorKey
            ) ??
              false) ||
            tutor.expired,
        }
      })
    })
  }, [filteredTutorOptions, programDetails.tutors])

  /** Methods */
  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    value?: string,
    validity?: FieldValidity
  ) => {
    const feeFields: ProgramFeeNames[] = [
      ProgramFeeNames.Tuition,
      ProgramFeeNames.EnrollmentFee,
      ProgramFeeNames.FacilityFeeFirstStudent,
      ProgramFeeNames.FacilityFeeAdditionalStudent,
      ProgramFeeNames.LocalFeeFirstStudent,
      ProgramFeeNames.LocalFeeAdditionalStudent,
      ProgramFeeNames.MiscFeeFirstStudent,
      ProgramFeeNames.MiscFeeAdditionalStudent,
      ProgramFeeNames.SupplyFeeFirstStudent,
      ProgramFeeNames.SupplyFeeAdditionalStudent,
      ProgramFeeNames.SubLicensedTutorPercentage,
      ProgramFeeNames.SemesterOneLicensingFee,
      ProgramFeeNames.DiscountSemesterOneLicensingFee,
      ProgramFeeNames.SemesterTwoLicensingFee,
      ProgramFeeNames.DiscountSemesterTwoLicensingFee,
      ProgramFeeNames.MultiStudentApplicationDiscount,
      ProgramFeeNames.InvitationFee,
      ProgramFeeNames.DiscountInvitationFee,
      ProgramFeeNames.TutorS1FirstStudentLicFee,
      ProgramFeeNames.TutorS1AdditionalStudentLicFee,
      ProgramFeeNames.TutorS2FirstStudentLicFee,
      ProgramFeeNames.TutorS2AdditionalStudentLicFee,
      ProgramFeeNames.LcdTutorPercentage,
    ]

    if (feeFields.includes(event.target.name as ProgramFeeNames)) {
      const value = event.target.value

      if (!isValidInputDecimal(value)) return

      setFees({
        ...fees,
        [event.target.name]: value,
      })
      setProgramDetails({
        ...programDetails,
        [event.target.name]: value,
      })
      setValidFields({
        ...validFields,
        [event.target.name]: { input: true },
      })
      setHasChangeFees(true)
      return
    }

    switch (event.target.name) {
      case 'capacity':
        setProgramDetails({
          ...programDetails,
          [event.target.name]: +event.target.value.replace(/[^0-9]/g, ''),
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'firstSemesterStartDate':
        setFirstSemesterStartDate(event.target.value)
        setValidFields({
          ...validFields,
          semesterOneStartDate: validity ?? {
            input: true,
            beforeMax: true,
            afterMin: true,
          },
        })
        break
      case 'secondSemesterStartDate':
        setSecondSemesterStartDate(event.target.value)
        setValidFields({
          ...validFields,
          semesterTwoStartDate: validity ?? {
            input: true,
            beforeMax: true,
            afterMin: true,
          },
        })

        if (event.target.value > semesterOneDate) {
          if (validity) validity.afterPrevious = true
        }

        break
      case 'dayOfTheWeek':
        setProgramDetails({
          ...programDetails,
          [event.target.name]: daysOfTheWeekMap.get(event.target.value) ?? 0,
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'tutorName':
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'type':
        setPreviousProgramType(programDetails.type)
        const programType = programTypeMap.get(
          event.target.value
        ) as ProgramType
        const resetForProgramType = programType !== programDetails.type
        setProgramDetails({
          ...programDetails,
          director: resetForProgramType ? '' : programDetails.director,
          directorActorId: resetForProgramType
            ? -1
            : programDetails.directorActorId,
          tutorName: resetForProgramType ? '' : programDetails.tutorName,
          [event.target.name]: programTypeMap.get(
            event.target.value
          ) as ProgramType,
        })
        setProgramType(event.target.value as ProgramType)
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'communityName':
        const communityProgramOption = communitiesMap.get(event.target.value)
        if (communityProgramOption) {
          setPreviousCommunityId(programDetails.communityId)
          setDateFieldsForCommunitySelection(communityProgramOption)
          setAddressFieldsForCommunitySelection(communityProgramOption)
        }
        const communityKey = !!communityProgramOption
          ? communityProgramOption.communityKey
          : -1
        const resetForCommunityChange =
          communityKey < 0 ||
          communityKey !== (programDetails.communityId ?? -1)
        setProgramDetails({
          ...programDetails,
          communityId: communityKey,
          // If the community is updating reset the director and tutor
          director: resetForCommunityChange ? '' : programDetails.director,
          directorActorId: resetForCommunityChange
            ? -1
            : programDetails.directorActorId,
          tutorName: resetForCommunityChange ? '' : programDetails.tutorName,
          address: !!communityProgramOption
            ? communityProgramOption.address
            : programDetails.address,
          [event.target.name]: event.target.value,
        })
        setLoadCommunityProgramOptions(true)
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'director':
        const resetForDirectorChange = true
        const [director] = event.target.value.split(' - ')
        setProgramDetails({
          ...programDetails,
          directorActorId: directorsMap.get(event.target.value),
          directorPhone:
            directors
              ?.filter(
                (actor) =>
                  actor.actorKey === directorsMap.get(event.target.value)
              )
              .map((director) => director.phone)
              .toString() ?? '',
          directorEmail:
            directors
              ?.filter(
                (actor) =>
                  actor.actorKey === directorsMap.get(event.target.value)
              )
              .map((director) => director.email)
              .toString() ?? '',
          tutorName: resetForDirectorChange ? '' : programDetails.tutorName,
          [event.target.name]: director,
        })

        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break

      case 'endTime':
        setProgramDetails({
          ...programDetails,
          [event.target.name]: event.target.value,
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
            afterPrevious: true,
          },
        })
        break

      case 'directorPhone':
        setProgramDetails({
          ...programDetails,
          [event.target.name]: event.target.value,
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      case 'tutorModel':
        setProgramDetails({
          ...programDetails,
          [event.target.name]: event.target.value,
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
      default:
        setProgramDetails({
          ...programDetails,
          [event.target.name]: event.target.value,
        })
        setValidFields({
          ...validFields,
          [event.target.name]: {
            input: true,
          },
        })
        break
    }
  }

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => {
    const amount = amountFromInput(event.target.value)
    setFees({
      ...fees,
      [event.target.name]: amount,
    })
    setProgramDetails({
      ...programDetails,
      [event.target.name]: amount,
    })
    setValidFields({
      ...validFields,
      [event.target.name]: {
        input: true,
      },
    })
    setHasChangeFees(true)
  }

  const fallbackForCommunityName = ({
    communityKey,
  }: Pick<ProgramCommunityOption, 'communityKey'>) => communityKey + ''

  const resetFields = useCallback(() => {
    setProgramDetails(
      !!props.programDetails ? props.programDetails : emptyProgramDetails
    )
    setAddress({
      ...props.programDetails.address,
    })

    setFirstSemesterStartDate(
      dateToDashString(
        !!props.programDetails.semesterOneStartDate
          ? props.programDetails.semesterOneStartDate
          : today,
        true
      )
    )

    setSecondSemesterStartDate(
      dateToDashString(
        !!props.programDetails.semesterTwoStartDate
          ? props.programDetails.semesterTwoStartDate
          : fourMonthsFromToday,
        true
      )
    )

    setFees({
      [ProgramFeeNames.ApplicationFee]:
        initialFees.applicationFee ?? fees.applicationFee,
      [ProgramFeeNames.Tuition]: initialFees.tuition ?? fees.tuition,
      [ProgramFeeNames.LocalFeeFirstStudent]:
        initialFees.localFeeFirstStudent ?? fees.localFeeFirstStudent,
      [ProgramFeeNames.LocalFeeAdditionalStudent]:
        initialFees.localFeeAdditionalStudent ?? fees.localFeeAdditionalStudent,
      [ProgramFeeNames.FacilityFeeFirstStudent]:
        initialFees.facilityFeeFirstStudent ?? fees.facilityFeeFirstStudent,
      [ProgramFeeNames.FacilityFeeAdditionalStudent]:
        initialFees.facilityFeeAdditionalStudent ??
        fees.facilityFeeAdditionalStudent,
      [ProgramFeeNames.SupplyFeeFirstStudent]:
        initialFees.supplyFeeFirstStudent ?? fees.supplyFeeFirstStudent,
      [ProgramFeeNames.SupplyFeeAdditionalStudent]:
        initialFees.supplyFeeAdditionalStudent ??
        fees.supplyFeeAdditionalStudent,
      [ProgramFeeNames.MiscFeeFirstStudent]:
        initialFees.miscFeeFirstStudent ?? fees.miscFeeFirstStudent,
      [ProgramFeeNames.MiscFeeAdditionalStudent]:
        initialFees.miscFeeAdditionalStudent ?? fees.miscFeeAdditionalStudent,
      [ProgramFeeNames.EnrollmentFee]:
        initialFees.enrollmentFee ?? fees.enrollmentFee,
      [ProgramFeeNames.SubLicensedTutorPercentage]:
        initialFees.subLicensedTutorPercentage ??
        fees.subLicensedTutorPercentage,
      [ProgramFeeNames.LcdTutorPercentage]:
        initialFees.lcdTutorPercentage ?? fees.lcdTutorPercentage,
      [ProgramFeeNames.SemesterOneLicensingFee]:
        initialFees.semesterOneLicensingFee ?? fees.semesterOneLicensingFee,
      [ProgramFeeNames.DiscountSemesterOneLicensingFee]:
        initialFees.discountSemesterOneLicensingFee ??
        fees.discountSemesterOneLicensingFee,
      [ProgramFeeNames.SemesterTwoLicensingFee]:
        initialFees.semesterTwoLicensingFee ?? fees.semesterTwoLicensingFee,
      [ProgramFeeNames.DiscountSemesterTwoLicensingFee]:
        initialFees.discountSemesterTwoLicensingFee ??
        fees.discountSemesterTwoLicensingFee,
      [ProgramFeeNames.MultiStudentApplicationDiscount]:
        initialFees.multiStudentApplicationDiscount ??
        fees.multiStudentApplicationDiscount,
      [ProgramFeeNames.InvitationFee]:
        initialFees.invitationFee ?? fees.invitationFee,
      [ProgramFeeNames.DiscountInvitationFee]:
        initialFees.discountInvitationFee ?? fees.discountInvitationFee,
      [ProgramFeeNames.TutorS1FirstStudentLicFee]:
        initialFees.tutorS1FirstStudentLicFee ??
        fees.tutorS1FirstStudentLicFee ??
        '0.00',
      [ProgramFeeNames.TutorS1AdditionalStudentLicFee]:
        initialFees.tutorS1AdditionalStudentLicFee ??
        fees.tutorS1AdditionalStudentLicFee ??
        '0.00',
      [ProgramFeeNames.TutorS2FirstStudentLicFee]:
        initialFees.tutorS2FirstStudentLicFee ??
        fees.tutorS2FirstStudentLicFee ??
        '0.00',
      [ProgramFeeNames.TutorS2AdditionalStudentLicFee]:
        initialFees.tutorS2AdditionalStudentLicFee ??
        fees.tutorS2AdditionalStudentLicFee ??
        '0.00',
    })
    setHasChangeFees(false)

    setValidFields({
      ...validFieldsInitialState,
    })
  }, [
    fees.applicationFee,
    fees.discountSemesterOneLicensingFee,
    fees.discountSemesterTwoLicensingFee,
    fees.enrollmentFee,
    fees.facilityFeeFirstStudent,
    fees.localFeeFirstStudent,
    fees.miscFeeFirstStudent,
    fees.facilityFeeAdditionalStudent,
    fees.localFeeAdditionalStudent,
    fees.miscFeeAdditionalStudent,
    fees.multiStudentApplicationDiscount,
    fees.semesterOneLicensingFee,
    fees.semesterTwoLicensingFee,
    fees.subLicensedTutorPercentage,
    fees.supplyFeeFirstStudent,
    fees.supplyFeeAdditionalStudent,
    fees.tuition,
    fees.invitationFee,
    fees.discountInvitationFee,
    fees.tutorS1FirstStudentLicFee,
    fees.tutorS1AdditionalStudentLicFee,
    fees.tutorS2FirstStudentLicFee,
    fees.tutorS2AdditionalStudentLicFee,
    fees.lcdTutorPercentage,
    initialFees.applicationFee,
    initialFees.discountSemesterOneLicensingFee,
    initialFees.discountSemesterTwoLicensingFee,
    initialFees.enrollmentFee,
    initialFees.facilityFeeFirstStudent,
    initialFees.facilityFeeAdditionalStudent,
    initialFees.localFeeFirstStudent,
    initialFees.localFeeAdditionalStudent,
    initialFees.miscFeeFirstStudent,
    initialFees.miscFeeAdditionalStudent,
    initialFees.multiStudentApplicationDiscount,
    initialFees.semesterOneLicensingFee,
    initialFees.semesterTwoLicensingFee,
    initialFees.subLicensedTutorPercentage,
    initialFees.supplyFeeFirstStudent,
    initialFees.supplyFeeAdditionalStudent,
    initialFees.tuition,
    initialFees.invitationFee,
    initialFees.discountInvitationFee,
    initialFees.tutorS1FirstStudentLicFee,
    initialFees.tutorS1AdditionalStudentLicFee,
    initialFees.tutorS2FirstStudentLicFee,
    initialFees.tutorS2AdditionalStudentLicFee,
    initialFees.lcdTutorPercentage,
    props.programDetails,
  ])

  const validateDayOfWeek = useCallback(() => {
    return (
      (programDetails.dayOfTheWeek as number) >= 0 &&
      (programDetails.dayOfTheWeek as number) < 7
    )
  }, [programDetails.dayOfTheWeek])

  const labelForDayOfTheWeek = (numericalDay: number) => {
    let dayOfTheWeek = ''
    daysOfTheWeek.forEach((day) => {
      if (day.id === numericalDay) {
        dayOfTheWeek = day.label
      }
    })
    return dayOfTheWeek
  }

  const fieldsDoValidate = (args?: { isDraftValidation?: boolean }) => {
    setFieldValidity(false, args?.isDraftValidation)
    const isFirstSemesterValid = validateSemesterDate()
    const isSecondSemesterValid = validateSemesterDate(false)
    return (
      // We currently care about program type, status, and community to not be undefined before saving a draft
      !!programDetails.type &&
      !!programDetails.communityName &&
      isFirstSemesterValid.input &&
      isFirstSemesterValid.beforeMax &&
      isFirstSemesterValid.afterMin &&
      isSecondSemesterValid.input &&
      isSecondSemesterValid.beforeMax &&
      isSecondSemesterValid.afterMin &&
      isSecondSemesterValid.afterPrevious &&
      // Only need to check one same date, since both will have the same boolean determination
      !isSecondSemesterValid.sameDate &&
      validateTime().input &&
      validateTime(false).input &&
      validateMaxCapacity(args?.isDraftValidation === true) &&
      !!programDetails.director &&
      // If we are saving a draft carry on
      (args?.isDraftValidation === true ||
        // Otherwise check the other fields' existence
        (validateDayOfWeek() &&
          !!address.streetAddress1 &&
          satisfiesFeeMinimum(ProgramFeeNames.ApplicationFee) &&
          satisfiesFeeMinimum(ProgramFeeNames.Tuition) &&
          satisfiesFeeMinimum(ProgramFeeNames.LocalFeeFirstStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.LocalFeeAdditionalStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.FacilityFeeFirstStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.FacilityFeeAdditionalStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.SupplyFeeFirstStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.SupplyFeeAdditionalStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.EnrollmentFee) &&
          satisfiesFeeMinimum(ProgramFeeNames.SubLicensedTutorPercentage) &&
          satisfiesFeeMinimum(ProgramFeeNames.LcdTutorPercentage) &&
          satisfiesFeeMinimum(ProgramFeeNames.MiscFeeFirstStudent) &&
          satisfiesFeeMinimum(ProgramFeeNames.MiscFeeAdditionalStudent)))
    )
  }

  const handleChangeFee = async () => {
    await handleActions(Action.Edit, ProgramStatus.Published)
  }

  const handleShowModalFee = () => {
    setShowFeeModalWarning(true)
  }

  const performAction = async (options: {
    status: ProgramStatus
    action: Action
  }) => {
    // Set the programId for edit if it's already set
    try {
      const isEdit = options.action === Action.Edit
      let message = ''
      if (
        isEdit &&
        hasEnrollmentInvitesWithInProgressStatus &&
        options.status === ProgramStatus.Draft
      ) {
        message = t(
          'Programs.ProgramForm.ErrorMessage.RevertToDraft',
          'This program can not be reverted to Draft because there are invitations still in progress.'
        )
        throw new Error(message)
      }
      const tutorIds = [...tutorActorIdMap.values()]
      if (tutorIds.length > 1) {
        const isDuplicate = new Set(tutorIds).size !== tutorIds.length

        if (isDuplicate) {
          message = t(
            'Programs.ProgramForm.ErrorMessage.TutorError',
            'Cannot add the same tutor multiple times'
          )
          throw new Error(message)
        }
      }
      const addUpdateProgramBody: ProgramUpdate = {
        ...programDetails,
        hideFromPublicSearch,
        semesterOneStartDate: jsDateFromYearMonthDayString(
          firstSemesterStartDate
        ),
        semesterTwoStartDate: jsDateFromYearMonthDayString(
          secondSemesterStartDate
        ),
        status: options.status,
        startTime: programDetails.startTime as string,
        endTime: programDetails.endTime as string,
        dayOfTheWeek:
          programDetails.dayOfTheWeek === 0
            ? 7
            : programDetails.dayOfTheWeek ?? 7,
        tutorActorIds: [...tutorActorIdMap.values()],
      }

      const createdProgramId = await addUpdateProgram(addUpdateProgramBody)
      if (isEdit) {
        message = t(
          'Programs.ProgramForm.SuccessMessage.UpdateProgram',
          'Program successfully updated.'
        )
      } else {
        message = t(
          'Programs.ProgramForm.SuccessMessage.CreateProgram',
          'Program successfully created.'
        )
      }

      if (!!createdProgramId) {
        handleMessage(message)
        handleSuccess({
          successType: options.action,
          programId: createdProgramId,
          status: options.status,
        })
      } else {
        handleMessage(errorMessage, 'error')
      }
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const handleActions = async (
    action: Action,
    status = ProgramStatus.Draft
  ) => {
    status = action === Action.Archived ? ProgramStatus.Archived : status
    if (
      fieldsDoValidate({
        isDraftValidation:
          action === Action.Draft || action === Action.Archived
            ? true
            : action === Action.Edit
            ? status === ProgramStatus.Draft
            : false,
      })
    ) {
      setLoading(true)

      if (
        // displays a confirmation modal when the user selects `Publish` and the tutor model is not `LCD`
        programDetails.tutorModel !== LCD &&
        (action === Action.Publish ||
          (action === Action.Edit &&
            status === ProgramStatus.Published &&
            programDetails.status !== ProgramStatus.Published))
      ) {
        setPendingAction({ action, status })
        setShowConfirmationModal(true)
        return
      }

      performAction({
        action,
        status,
      })
    } else {
      setShowFeeModalWarning(false)
      handleMessage(errorMessage, 'error')
    }
    setLoading(false)
  }

  const handleSuccess = (args: {
    successType: Action
    programId: number
    status: ProgramStatus
  }) => {
    setIsFieldDisabled(true)
    setStatus(args.status)
    switch (args.successType) {
      case Action.Publish:
      case Action.Draft:
        navigate({
          pathname: `/communities/community-details/${selectedCommunityKey}/programs/program-details/${args.programId}`,
        })
        break
      case Action.Archived:
        navigate(
          {
            pathname: `/communities/community-details/${selectedCommunityKey}/overview`,
          },
          {
            state: {
              communityKey: selectedCommunityKey,
              name: programDetails.communityName,
            },
          }
        )
        break
      case Action.Edit:
        props.handleSave?.()
        break
    }
  }

  const satisfiesFeeMinimum = useCallback(
    (feeName: ProgramFeeNames) => {
      return convertTwoDecimalStringToBig(fees[feeName]).gte(
        convertTwoDecimalStringToBig(
          programFeeMinimaMap.get(feeName)?.minimum ?? '0.00'
        )
      )
    },
    [fees, programFeeMinimaMap]
  )

  const validateSemesterDate = useCallback(
    (isFirstSemester = true): FieldValidity => {
      const isValidFirstSemesterDate = dateUtilities.isValidDateString(
        firstSemesterStartDate
      )
      const isValidSecondSemesterDate = dateUtilities.isValidDateString(
        secondSemesterStartDate
      )
      const isFirstSemesterBeforeSecond =
        dateUtilities.isFirstDateStringBeforeOrAtSecond(
          createSafeDate(firstSemesterStartDate),
          createSafeDate(secondSemesterStartDate)
        )
      const isFirstSemesterInRange = isWithinProgramDateRange(
        firstSemesterStartDate,
        MINIMUM_SEMESTER_DATE,
        MAXIMUM_SEMESTER_DATE
      )
      const isSecondSemesterInRange = isWithinProgramDateRange(
        secondSemesterStartDate,
        MINIMUM_SEMESTER_DATE,
        MAXIMUM_SEMESTER_DATE
      )
      const sameDate = isSameDate(
        createSafeDate(firstSemesterStartDate),
        createSafeDate(secondSemesterStartDate)
      )

      return isFirstSemester
        ? {
            input: isValidFirstSemesterDate,
            ...isFirstSemesterInRange,
            sameDate,
          }
        : {
            input: isValidSecondSemesterDate,
            afterPrevious: isFirstSemesterBeforeSecond,
            ...isSecondSemesterInRange,
            sameDate,
          }
    },
    [firstSemesterStartDate, secondSemesterStartDate]
  )

  const validateTime = useCallback(
    (isStartTime = true): FieldValidity => {
      const timeRegex = /([0-1][0-9]|2[0-3]):([0-5][0-9])/
      const startTimeIsValid = timeRegex.test(programDetails.startTime ?? '')
      const endTimeIsValid = timeRegex.test(programDetails.endTime ?? '')
      // If start time is invalid, we cannot validate either time
      if (isStartTime) {
        return { input: !!startTimeIsValid }
      }
      // Otherwise verify end time is a valid time
      else if (!isStartTime && endTimeIsValid) {
        const startTimeParts = (programDetails.startTime ?? '').split(':')
        const startTimeAsDate = new Date()
        startTimeAsDate.setHours(parseInt(startTimeParts[0]))
        startTimeAsDate.setMinutes(parseInt(startTimeParts[1]))
        const endTimeParts = (programDetails.endTime ?? '').split(':')
        const endTimeAsDate = new Date()
        endTimeAsDate.setHours(parseInt(endTimeParts[0]))
        endTimeAsDate.setMinutes(parseInt(endTimeParts[1]))
        const endTimeBeforeStartTime =
          startTimeIsValid && endTimeAsDate <= startTimeAsDate

        return {
          input: !!endTimeIsValid,
          afterPrevious: !endTimeBeforeStartTime,
        }
      }

      return { input: false }
    },
    [programDetails.endTime, programDetails.startTime]
  )

  const validateMaxCapacity = useCallback(
    (isDraft?: boolean) => {
      /**
       * If we are saving capacity in draft, we do allow a value of 0.
       * When we are publishing, then capacity must be greater than 0
       * and lower than 1000.
       */
      const maxCapacity = programDetails.capacity || 0
      const isGreaterOrEqualsThanMin = maxCapacity >= 1
      const isLessOrEqualsThanMax = maxCapacity <= 999
      const isValueInRange = isGreaterOrEqualsThanMin && isLessOrEqualsThanMax

      return isDraft ? isLessOrEqualsThanMax : isValueInRange
    },
    [programDetails.capacity]
  )

  const setFieldValidity = useCallback(
    (overrideValue = false, draftValidation = false) => {
      const isValidSemesterOneDate = validateSemesterDate(),
        isValidSemesterTwoDate = validateSemesterDate(false),
        isValidStartTime = validateTime(),
        isValidEndTime = validateTime(false)
      setValidFields({
        // Only care about validating program type and community if draft
        type: { input: !!programDetails.type || overrideValue },
        communityName: {
          input: !!programDetails.communityName || overrideValue,
        },
        semesterOneStartDate: isValidSemesterOneDate || {
          input: overrideValue,
          beforeMax: overrideValue,
          afterMin: overrideValue,
        },
        semesterTwoStartDate: isValidSemesterTwoDate || {
          input: overrideValue,
          beforeMax: overrideValue,
          afterMin: overrideValue,
          afterPrevious: overrideValue,
        },
        startTime: isValidStartTime || { input: overrideValue },
        endTime: isValidEndTime || {
          input: overrideValue,
          afterPrevious: overrideValue,
        },
        dayOfTheWeek: {
          input: validateDayOfWeek() || overrideValue || draftValidation,
        },
        capacity: {
          input: validateMaxCapacity() || overrideValue || draftValidation,
        },
        director: {
          input: !!programDetails.director || overrideValue,
        },
        tutorName: {
          input: !!programDetails.tutorName || overrideValue || draftValidation,
        },
        tutorActorId: {
          input:
            !!programDetails.tutorActorId || overrideValue || draftValidation,
        },
        tutorModel: {
          input: !!programDetails.tutorModel || overrideValue,
        },
        directorPhone: {
          input: !!programDetails.directorPhone,
        },
        // Validate fees if publishing
        [ProgramFeeNames.ApplicationFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.ApplicationFee),
        },
        [ProgramFeeNames.Tuition]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.Tuition),
        },
        [ProgramFeeNames.EnrollmentFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.EnrollmentFee),
        },
        [ProgramFeeNames.SupplyFeeFirstStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.SupplyFeeFirstStudent),
        },
        [ProgramFeeNames.SupplyFeeAdditionalStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.SupplyFeeAdditionalStudent),
        },
        [ProgramFeeNames.LocalFeeFirstStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.LocalFeeFirstStudent),
        },
        [ProgramFeeNames.LocalFeeAdditionalStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.LocalFeeAdditionalStudent),
        },
        [ProgramFeeNames.FacilityFeeFirstStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.FacilityFeeFirstStudent),
        },
        [ProgramFeeNames.FacilityFeeAdditionalStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.FacilityFeeAdditionalStudent),
        },
        [ProgramFeeNames.MiscFeeFirstStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.MiscFeeFirstStudent),
        },
        [ProgramFeeNames.MiscFeeAdditionalStudent]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.MiscFeeAdditionalStudent),
        },
        [ProgramFeeNames.SubLicensedTutorPercentage]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.SubLicensedTutorPercentage),
        },
        [ProgramFeeNames.LcdTutorPercentage]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.LcdTutorPercentage),
        },
        [ProgramFeeNames.SemesterOneLicensingFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.SemesterOneLicensingFee),
        },
        [ProgramFeeNames.DiscountSemesterOneLicensingFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(
              ProgramFeeNames.DiscountSemesterOneLicensingFee
            ),
        },
        [ProgramFeeNames.SemesterTwoLicensingFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.SemesterTwoLicensingFee),
        },
        [ProgramFeeNames.DiscountSemesterTwoLicensingFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(
              ProgramFeeNames.DiscountSemesterTwoLicensingFee
            ),
        },
        [ProgramFeeNames.MultiStudentApplicationDiscount]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(
              ProgramFeeNames.MultiStudentApplicationDiscount
            ),
        },
        [ProgramFeeNames.InvitationFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.InvitationFee),
        },
        [ProgramFeeNames.DiscountInvitationFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.DiscountInvitationFee),
        },
        [ProgramFeeNames.TutorS1FirstStudentLicFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.TutorS1FirstStudentLicFee),
        },
        [ProgramFeeNames.TutorS1AdditionalStudentLicFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.TutorS1AdditionalStudentLicFee),
        },
        [ProgramFeeNames.TutorS2FirstStudentLicFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.TutorS2FirstStudentLicFee),
        },
        [ProgramFeeNames.TutorS2AdditionalStudentLicFee]: {
          input:
            !programDetails.communityName ||
            draftValidation ||
            satisfiesFeeMinimum(ProgramFeeNames.TutorS2AdditionalStudentLicFee),
        },
      })

      // Otherwise, mark the rest of the fields valid/invalid
      if (!draftValidation) {
        setIsAddressValid(!!address.streetAddress1 || overrideValue)
      } else {
        // Handle case: failed to publish, so saved draft. Set any invalid fields to valid since we don't care.
        setIsAddressValid(true)
      }
    },
    [
      address.streetAddress1,
      programDetails.communityName,
      programDetails.director,
      programDetails.directorPhone,
      programDetails.tutorName,
      programDetails.tutorActorId,
      programDetails.type,
      programDetails.tutorModel,
      satisfiesFeeMinimum,
      validateDayOfWeek,
      validateMaxCapacity,
      validateSemesterDate,
      validateTime,
    ]
  )

  const handleCancel = useCallback(() => {
    setIsFieldDisabled(true)
    setFieldValidity(true)
    resetFields()
    setIsCancel(true)
    setShowFeeModalWarning(false)
    // Reset previous program type and community
    setPreviousCommunityId(
      !!props.programDetails
        ? props.programDetails.communityId
        : emptyProgramDetails.communityId
    )
    setPreviousProgramType(
      !!props.programDetails
        ? props.programDetails.type
        : emptyProgramDetails.type
    )
    setTutorFormFields([])
    setHasChangeFees(false)
  }, [props.programDetails, resetFields, setFieldValidity])

  /**
   * If we have successfully confirmed a cancellation on edit, complete the actions,
   * otherwise keep the state of the form.
   */
  useEffect(() => {
    if (cancelInitiated) {
      setCancelInitiated(false)
      handleCancel()
    }
  }, [cancelInitiated, setCancelInitiated, handleCancel])

  const editLocation = () => {
    setIsAddressModalOpen(true)
  }

  /**
   * This variable is used to display buttons based on program status
   * Save Draft button should show when the program current status is archive or draft
   * */
  const isDraftProgram = status === ProgramStatus.Draft
  const isArchiveProgram = status === ProgramStatus.Archived

  const errorMessage = t(
    'Programs.ProgramForm.ErrorMessage',
    'Something went wrong. Please make sure you have filled out the required fields.'
  )

  const addTutorField = () => {
    const maxTutorCapacity = 99
    if (tutorFormFields.length < maxTutorCapacity) {
      const dateTime = new Date().getTime()
      const tutorFormKey = `tutorFormKey-${dateTime}`
      setTutorFormFields([
        ...tutorFormFields,
        <TutorFormField
          key={`tutorFormFieldKey-${dateTime}`}
          tutors={tutorSelectionOptions}
          isDisabled={isTutorFieldDisabled}
          tutorFormKey={tutorFormKey}
          tutorFormId={`tutorFormId-${dateTime}`}
          removeTutorField={removeTutorField}
          onTutorChange={onTutorChange}
          tutorName={''}
          programTutors={programDetails.tutors}
        />,
      ])
    }
  }

  const handleEdit = () => {
    setIsFieldDisabled(false)
    setLoadProgramOptions(true)
    setLoading(true)
    setLoadingProgramFormCard(false)
  }

  /** Boolean determinations */
  const isProgramTypeDisabled =
    isFieldDisabled || isEmpty(programDetails.communityName)
  const isFeeFieldDisabled =
    isProgramTypeDisabled || isEmpty(programDetails.type)

  // RETURNS
  if (
    (loadingIds.has(fetchCommunitySpecificProgramOptionsLoadingId) ||
      loadingIds.has(fetchProgramOptionsLoadingId)) &&
    !isLoadingProgramFormCard
  ) {
    return <LoadingProgress />
  }

  if (
    (props.programAbility?.can('edit', 'Program') ||
      props.programAbility?.can('create', 'Program')) &&
    loading &&
    !isProgramDetailsLoading &&
    !directors &&
    !tutorOptions
  ) {
    return <LoadingProgress />
  }

  return (
    <>
      <BasicModal
        isOpen={showFeeModalWarning}
        maxWidth={'xs'}
        dialogTitle={t(
          'ProgramFromCard.ConfirmationModal.ProgramsFeeUpdate.Title',
          'Program Fees will be updated'
        )}
        dialogContent={
          <Box maxWidth={300} mx="auto" textAlign="center">
            <Typography component="p">
              {t(
                'ProgramFromCard.ConfirmationModal.ProgramsFeeUpdate.Content',
                '*Warning : You are about to change the fees for this program. This will change the fee and balances on all existing parent invoices. Would you like to proceed? '
              )}
            </Typography>
          </Box>
        }
        handleFormSubmit={handleChangeFee}
        dialogActions={
          <ActionButtons
            primaryButtonLabel={ContainedButtonVariant.YesProceed}
            secondaryButtonLabel={TextButtonVariant.NoCancel}
            secondaryClick={handleCancel}
          />
        }
      />
      <BasicModal
        isOpen={showConfirmationModal}
        dialogTitle={t('ProgramFormCard.ConfirmationModal.Title', 'STOP!')}
        dialogContent={
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" component="h1">
              {t(
                'ProgramFormCard.ConfirmationModal.Content.Message',
                'You selected {{tutorModel}} model.',
                { tutorModel: programDetails.tutorModel }
              )}
            </Typography>
            <Typography variant="h6" component="h1">
              {t(
                'ProgramFormCard.ConfirmationModal.Content.Question',
                'Is this correct?'
              )}
            </Typography>
            <Typography variant="h6" component="p" mt={4} fontSize={14}>
              {t(
                'ProgramFormCard.ConfirmationModal.Content.Note',
                'NOTE: if your community is changing to the LCD model, the model should be changed at the community level, then all newly created programs will default to LCD model.'
              )}
            </Typography>
          </Box>
        }
        handleFormSubmit={() => {
          if (pendingAction) {
            performAction(pendingAction)
            setPendingAction(null)
          }
          setShowConfirmationModal(false)
          setLoading(false)
        }}
        dialogActions={
          <>
            <Button
              color="primary"
              variant="outlined"
              sx={{ marginX: 2 }}
              fullWidth
              type={'submit'}
            >
              {t(
                'ProgramFormCard.ConfirmationModal.CorrectModel',
                '{{tutorModel}}\n is the correct model',
                {
                  tutorModel: programDetails?.tutorModel,
                }
              )}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              sx={{ marginX: 2 }}
              fullWidth
              onClick={() => setShowConfirmationModal(false)}
            >
              {t(
                'ProgramFormCard.ConfirmationModal.SelectModel',
                'I need to select LCD model'
              )}
            </Button>
          </>
        }
        maxWidth="sm"
      />
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false)
        }}
        initialAddress={address}
        onAddressConfirm={(value) => {
          setProgramDetails({
            ...programDetails,
            address: value,
          })
          setAddress(value)
        }}
      />
      <Card
        sx={{
          margin: 'auto',
          minHeight: 200,
          borderTop: `12px solid ${theme.palette.program.challenge}`,
          color: theme.palette.primary.main,
          padding: theme.spacing(3, 4),
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(2),
          },
          ...(programDetails.type === 'Foundations' && {
            borderTop: `12px solid ${theme.palette.program.foundations}`,
          }),
          ...(programDetails.type === 'Essentials' && {
            borderTop: `12px solid ${theme.palette.program.essentials}`,
          }),
          ...(programDetails.type === 'Scribblers' && {
            borderTop: `12px solid ${theme.palette.program.scribblers}`,
          }),
        }}
      >
        <section id="programFormCardButtons">
          <CardFormHeader
            header={
              <>
                <div className={programStatusLabel[status]} />
                <Typography
                  variant="overline"
                  component="p"
                  sx={{
                    display: 'inline',
                    color: theme.palette.primary.dark,
                  }}
                >
                  {programStatusLabel[status]}
                </Typography>
              </>
            }
            buttons={
              (props.programAbility?.can('edit', 'Program') ||
                props.programAbility?.can('publish', 'Program') ||
                (useAddProgramVariant &&
                  permissionAbility.can('create', 'Program'))) && (
                <ProgramFormCardButtons
                  isFieldDisabled={isFieldDisabled}
                  isDraftProgram={isDraftProgram}
                  handleEdit={handleEdit}
                  handleCancel={props.handleCancel ?? handleCancel}
                  handleActions={handleActions}
                  useAddProgramVariant={useAddProgramVariant}
                  hasEnrollment={props.hasEnrollment}
                  isArchiveProgram={isArchiveProgram}
                  hasChangeFees={hasChangeFees}
                  handleShowModalFee={handleShowModalFee}
                />
              )
            }
            headerContainerProps={{ margin: 0 }}
          />
        </section>
        <ProgramInformationFields
          programInformationDetails={{
            ...programDetails,
            programType,
            firstSemesterStartDate,
            secondSemesterStartDate,
            tutorModel: programDetails.tutorModel ?? tutorModel,
          }}
          programTypes={programTypes ?? []}
          programModelsOptions={tutorModelsOptions ?? []}
          isFieldDisabled={isFieldDisabled}
          minimumSemesterDate={MINIMUM_SEMESTER_DATE}
          maximumSemesterDate={MAXIMUM_SEMESTER_DATE}
          handleInputChange={handleInputChange}
          labelForDayOfTheWeek={labelForDayOfTheWeek}
          validFields={validFields}
          isProgramTypeDisabled={isProgramTypeDisabled}
          daysOfTheWeek={daysOfTheWeek}
          filteredTutorOptions={filteredTutorOptions}
          filteredDirectors={filteredDirectors}
          communitiesMap={communitiesMap}
          useAddProgramVariant={useAddProgramVariant}
          tutors={programDetails.tutors}
          onTutorChange={onTutorChange}
          onTutorRemoveField={onTutorFieldRemove}
          isCancel={isCancel}
          tutorFormFields={tutorFormFields}
          addTutorField={addTutorField}
          hideFromPublicSearch={hideFromPublicSearch}
          onHideFromPublicSearchChange={setHideFromPublicSearch}
        />
        <FormDivider />
        <ProgramAddressFields
          address={address}
          isAddressValid={isAddressValid}
          isFieldDisabled={isFieldDisabled}
          editLocation={editLocation}
        />
        <FormDivider />
        <ProgramFeeFields
          fees={fees}
          programFeeMinimaMap={programFeeMinimaMap}
          isFeeFieldDisabled={isFeeFieldDisabled}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          programAbility={props.programAbility}
          validFields={validFields}
          useAddProgramVariant={useAddProgramVariant}
        />
      </Card>
    </>
  )
}

export default ProgramFormCard
